import React, { useState } from 'react';
import { Building2, Calendar, Star, ChevronRight, Info } from 'lucide-react';
import ScoreBadge from './ScoreBadge';
import PendingScoreBadge from './PendingScoreBadge';
import { EmailButton } from './ResendEmailButton';
import CopyLinkButton from './CopyLinkButton';
import ReferenceTableHeader from './ReferenceTableHeader';
import ViewAnswersButton from './ViewAnswersButton';
import AddReferenceButton from './AddReferenceButton';
import ReferenceAnswersModal from './ReferenceAnswersModal';
import ReferenceContactModal from './ReferenceContactModal';
import { ReferenceWithAnswers } from '../../api/references';
import { referencesApi } from '../../api/references';
import { useLanguage } from '../../contexts/LanguageContext';

interface ReferencesTableProps {
  references: ReferenceWithAnswers[];
  candidateId?: string;
  jobId?: string;
  isJobCompleted?: boolean;
  onReferenceAdded?: () => void;
}

// Ensure we have a valid array of references
const ensureValidReferences = (references: ReferenceWithAnswers[] | null | undefined): ReferenceWithAnswers[] => {
  if (!references || !Array.isArray(references)) {
    return [];
  }
  return references;
};

// Interface for the old reference format used by UI components
interface LegacyReferenceFormat {
  id: string;
  candidateId: string;
  referentName: string;
  referentTitle: string;
  relationship: string;
  company: string;
  skills: Record<string, number>;
  lastEmailDate: string;
  submittedAt: string;
}

const ReferencesTable = ({ references: rawReferences, candidateId, jobId, isJobCompleted = false, onReferenceAdded }: ReferencesTableProps) => {
  const { t } = useLanguage();
  // Ensure we have a valid array of references
  const references = ensureValidReferences(rawReferences);
  
  const [selectedReference, setSelectedReference] = useState<LegacyReferenceFormat | null>(null);
  const [resendingEmailMap, setResendingEmailMap] = useState<Record<string, boolean>>({});
  const [viewingAnswersReference, setViewingAnswersReference] = useState<ReferenceWithAnswers | null>(null);
  const [viewingContactReference, setViewingContactReference] = useState<ReferenceWithAnswers | null>(null);

  // Function to adapt API reference to the format expected by UI components
  const adaptReference = (reference: ReferenceWithAnswers): LegacyReferenceFormat => {
    // Extract skills from answers
    const skills: Record<string, number> = {};
    reference.answers.forEach(answer => {
      // Try to parse numeric scores from answers
      // This is a simple adaptation that assumes scores are stored in the answer text
      const scoreMatch = answer.textAnswer.match(/(\d+(\.\d+)?)/);
      if (scoreMatch) {
        skills[answer.questionId] = parseFloat(scoreMatch[0]);
      } else {
        // Default score if none found
        skills[answer.questionId] = 0;
      }
    });

    return {
      id: reference.id,
      candidateId: reference.candidate_id,
      referentName: reference.full_name,
      referentTitle: reference.department, // Using department as title
      relationship: reference.relationship_type,
      company: reference.company,
      skills: skills,
      lastEmailDate: reference.sent_email_at || new Date().toISOString(),
      submittedAt: reference.filled_form_at || new Date().toISOString()
    };
  };

  const calculateOverallScore = (reference: ReferenceWithAnswers): number => {
    // Extract numeric scores from answers
    const scores = reference.answers
      .map(answer => {
        const scoreMatch = answer.textAnswer.match(/(\d+(\.\d+)?)/);
        return scoreMatch ? parseFloat(scoreMatch[0]) : 0;
      })
      .filter(score => score > 0);
    
    if (scores.length === 0) return 0;
    return Number((scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1));
  };

  const hasAnsweredQuestions = (reference: ReferenceWithAnswers): boolean => {
    return reference.filled_form_at !== null && reference.answers.length > 0;
  };

  const handleSendReferenceEmail = async (referenceId: string) => {
    setResendingEmailMap(prev => ({ ...prev, [referenceId]: true }));
    try {
      // Call the API to send the email
      await referencesApi.resendEmail(referenceId);
      console.log('Email sent successfully for reference:', referenceId);
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setResendingEmailMap(prev => ({ ...prev, [referenceId]: false }));
    }
  };

  // Empty state
  if (references.length === 0) {
    return (
      <div className="py-6 px-4">
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-500">{t('candidates.referenceModals.referencesTable.noReferences')}</p>
          {candidateId && !isJobCompleted && (
            <AddReferenceButton
              candidateId={candidateId}
              onReferenceAdded={onReferenceAdded}
              disabled={isJobCompleted}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div>
          <div className="flex justify-between items-center mb-4">
            <ReferenceTableHeader references={references} />
            {candidateId && !isJobCompleted && (
              <AddReferenceButton
                candidateId={candidateId}
                onReferenceAdded={onReferenceAdded}
                disabled={isJobCompleted}
              />
            )}
          </div>
          <div className="max-h-[50vh] overflow-hidden" style={{display: "block", width: "100%", overflowY: "auto", overflowX: "auto"}}>
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-gray-50/50 border-b">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: "30%"}}>
                    {t('candidates.referenceModals.referencesTable.columns.referent')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: "20%"}}>
                    {t('candidates.referenceModals.referencesTable.columns.company')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: "30%"}}>
                    {t('candidates.referenceModals.referencesTable.columns.submitted')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider" style={{width: "20%"}}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {references.map((reference) => {
                  const hasAnswered = hasAnsweredQuestions(reference);
                  const overallScore = calculateOverallScore(reference);
                  // Format submitted date or use empty string if null
                  const submittedDate = reference.filled_form_at 
                    ? new Date(reference.filled_form_at).toLocaleDateString() 
                    : '';

                  return (
                    <tr key={reference.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <button 
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left transition-colors flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingContactReference(reference);
                            }}
                          >
                            {reference.full_name}
                            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500" />
                          </button>
                          <div className="text-sm text-gray-500">
                            {t(`departments.${reference.department}`)}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {t(`relationships.${reference.relationship_type}`)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{reference.company}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-2" />
                          {reference.filled_form_at ? new Date(reference.filled_form_at).toLocaleDateString() : t('common.notSubmitted')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3 flex-wrap">
                          {hasAnswered ? (
                            <ViewAnswersButton 
                              onClick={() => setViewingAnswersReference(reference)}
                            />
                          ) : (
                            <>
                              <EmailButton 
                                onSend={() => handleSendReferenceEmail(reference.id)}
                                isLoading={resendingEmailMap[reference.id] || false}
                                disabled={isJobCompleted}
                                email={reference.email}
                                label={t('candidates.referenceModals.candidateReferenceModal.actions.resend')}
                                title={t('candidates.referenceModals.candidateReferenceModal.actions.resendTitle')}
                              />
                              <CopyLinkButton
                                referenceId={reference.id}
                                disabled={isJobCompleted}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal for viewing reference answers */}
      {viewingAnswersReference && jobId && (
        <ReferenceAnswersModal
          reference={viewingAnswersReference}
          jobId={jobId}
          isOpen={!!viewingAnswersReference}
          onClose={() => setViewingAnswersReference(null)}
        />
      )}

      {/* Modal for viewing reference contact info */}
      {viewingContactReference && (
        <ReferenceContactModal
          reference={viewingContactReference}
          isOpen={!!viewingContactReference}
          onClose={() => setViewingContactReference(null)}
        />
      )}
    </>
  );
};

export default ReferencesTable;